/**
 * ScrollToTop - Smooth scroll to top button with cool animation
 * Shows when user scrolls down and provides smooth scroll back to top
 * Positions itself above CreatePromptFAB when both are visible
 */

import { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';

interface ScrollToTopProps {
  showAfter?: number;
  className?: string;
}

export default function ScrollToTop({ showAfter = 300, className = '' }: ScrollToTopProps) {
  const [isVisible, setIsVisible] = useState(false);
  const location = useLocation();

  // Check if CreatePromptFAB is visible (it's visible on all pages except excluded paths)
  const hideCreateFABOnPaths = ['/prompts/create', '/login', '/register', '/auth'];
  const isCreateFABVisible = !hideCreateFABOnPaths.some(path => location.pathname.startsWith(path));

  useEffect(() => {
    const toggleVisibility = () => {
      if (window.pageYOffset > showAfter) {
        setIsVisible(true);
      } else {
        setIsVisible(false);
      }
    };

    window.addEventListener('scroll', toggleVisibility);

    return () => {
      window.removeEventListener('scroll', toggleVisibility);
    };
  }, [showAfter]);

  const scrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth',
    });
  };

  if (!isVisible) {
    return null;
  }

  // Position above CreatePromptFAB if it's visible
  const bottomPosition = isCreateFABVisible ? 'bottom-24' : 'bottom-6';

  return (
    <button
      onClick={scrollToTop}
      className={`
        fixed ${bottomPosition} right-6 z-50
        w-12 h-12 
        bg-primary-600 hover:bg-primary-700 
        text-white 
        rounded-full 
        shadow-lg hover:shadow-xl
        transition-all duration-300 ease-in-out
        transform hover:scale-110 active:scale-95
        focus:outline-none focus:ring-4 focus:ring-primary-300 focus:ring-opacity-50
        group
        ${className}
      `}
      aria-label="Scroll to top"
      title="Back to top"
    >
      <div className="flex items-center justify-center h-full">
        <svg 
          className="w-6 h-6 transform transition-transform duration-300 group-hover:-translate-y-1" 
          fill="none" 
          stroke="currentColor" 
          viewBox="0 0 24 24"
        >
          <path 
            strokeLinecap="round" 
            strokeLinejoin="round" 
            strokeWidth={2.5} 
            d="M5 10l7-7m0 0l7 7m-7-7v18" 
          />
        </svg>
      </div>
      
      <div className="absolute inset-0 rounded-full bg-primary-400 opacity-0 group-hover:opacity-20 transition-opacity duration-300 animate-pulse"></div>
    </button>
  );
}